import api from '@seller-hermes/utils/api';
import { isStandaloneLoginRuntime } from '@seller-hermes/utils/runtime';
import {
  ACTIVE_SELLER_GROUP_HEADER,
  clearActiveSellerGroupId,
  getActiveProduct,
  getActiveSellerGroupId,
  getLocalPortalContext,
  setActiveSellerGroupId,
  setValidSellerGroupIds,
} from './portal-local-context';
import { PortalService } from './portal.api';
import type {
  PortalMeResponse,
  PortalProfile,
  PortalSellerGroupContext,
} from './portal.types';

const PORTAL_PROFILES: PortalProfile[] = ['LOG', 'GO', 'PAY'];

export interface DashboardContextBootstrap {
  portalMe: PortalMeResponse;
  product: PortalProfile;
  isHomologation: boolean;
  sellerGroup: PortalSellerGroupContext;
}

export class DashboardContextRedirect extends Error {
  constructor(public readonly url: string) {
    super(`Redirecting to ${url}`);
  }
}

let bootstrapPromise: Promise<DashboardContextBootstrap | null> | null = null;
let bootstrappedContext: DashboardContextBootstrap | null = null;

const normalizeProduct = (value?: string | null): PortalProfile | null => {
  const normalized = value?.toUpperCase();
  return PORTAL_PROFILES.includes(normalized as PortalProfile)
    ? (normalized as PortalProfile)
    : null;
};

export const getDashboardProduct = (): PortalProfile | null => {
  const localContext = getLocalPortalContext();
  if (isStandaloneLoginRuntime() && localContext?.product) {
    return localContext.product;
  }

  const configuredProduct = normalizeProduct(
    process.env['NX_PRODUCT'] || process.env['VITE_PRODUCT'],
  );

  if (configuredProduct) return configuredProduct;

  if (localContext?.product) return localContext.product;

  if (typeof window !== 'undefined') {
    const hostnameParts = window.location.hostname.toLowerCase().split('.');
    if (hostnameParts.includes('log')) return 'LOG';
    if (hostnameParts.includes('go')) return 'GO';
    if (hostnameParts.includes('pay')) return 'PAY';
  }

  // Sem subdominio e sem ?product= na URL atual (localhost fora da rota de
  // handoff, ou F5 numa rota interna): sem isso o product ficava null e
  // bootstrapDashboardContext caia no branch de "pega o primeiro seller group
  // disponivel", descartando a troca de contexto feita pelo usuario em vez de
  // revalidar o group guardado via selectSellerGroup.
  return getActiveProduct();
};

const getPortalUrl = () => process.env['NX_PORTAL_URL'] ?? '';

const getProductCandidates = (
  portalMe: PortalMeResponse,
  product: PortalProfile,
): PortalSellerGroupContext[] =>
  portalMe.seller_groups.filter(
    (context) => context.profile === product && context.status !== 'unavailable',
  );

const selectSellerGroup = (
  portalMe: PortalMeResponse,
  product: PortalProfile,
) => {
  const candidates = getProductCandidates(portalMe, product);

  if (!candidates.length) return null;

  const activeSellerGroupId = getActiveSellerGroupId();
  const stored = activeSellerGroupId
    ? candidates.find((context) => context.id === activeSellerGroupId)
    : undefined;

  // O cookie/memoria pode ter ficado apontando pra um grupo que o /portal/me
  // nao lista mais entre os candidatos validos (o user trocou de conta/grupo e
  // perdeu o acesso). Descarta o valor velho pra nao insistir nele: sem isso a
  // camada em memoria do getActiveSellerGroupId continuaria devolvendo o id
  // morto ate o fim do tab.
  if (activeSellerGroupId && !stored) {
    clearActiveSellerGroupId();
  }

  return (
    stored ||
    candidates.find((context) => context.is_default) ||
    candidates.find((context) => !context.is_homologation) ||
    candidates[0]
  );
};

const selectFirstAvailableSellerGroup = (
  portalMe: PortalMeResponse,
): { sellerGroup: PortalSellerGroupContext; product: PortalProfile } | null => {
  const first = portalMe.seller_groups.find(
    (context) => context.status !== 'unavailable' && context.profile !== 'PAY',
  );
  if (!first) return null;
  return { sellerGroup: first, product: first.profile };
};

export const getBootstrappedContext = () => bootstrappedContext;

export const getBootstrappedSellerGroup = () =>
  bootstrappedContext?.sellerGroup ?? null;

export const resetDashboardContextBootstrap = () => {
  bootstrapPromise = null;
  bootstrappedContext = null;
  clearActiveSellerGroupId();
  delete api.defaults.headers.common[ACTIVE_SELLER_GROUP_HEADER];
};

export const bootstrapDashboardContext =
  async (): Promise<DashboardContextBootstrap | null> => {
    if (bootstrappedContext) return bootstrappedContext;
    if (bootstrapPromise) return bootstrapPromise;

    bootstrapPromise = (async () => {
      const product = getDashboardProduct();

      if (product === 'PAY') {
        return null;
      }

      if (!product && !isStandaloneLoginRuntime()) {
        return null;
      }

      try {
        const portalMe = await PortalService.getMe();

        let resolvedProduct: PortalProfile | null = product;
        let sellerGroup: PortalSellerGroupContext | null | undefined;

        if (resolvedProduct) {
          sellerGroup = selectSellerGroup(portalMe, resolvedProduct);
        } else {
          // Runtime standalone sem produto na URL: pega o primeiro seller
          // group disponível
          const first = selectFirstAvailableSellerGroup(portalMe);
          sellerGroup = first?.sellerGroup ?? null;
          resolvedProduct = first?.product ?? null;
        }

        if (!sellerGroup || !resolvedProduct) {
          // Nenhum grupo valido pro produto: nao deixa um cookie velho apontando
          // pra um grupo perdido sobreviver pro proximo load/aba.
          clearActiveSellerGroupId();
          throw new DashboardContextRedirect(getPortalUrl());
        }

        // Register the candidates this user actually belongs to for this
        // product *before* writing the cookie, so getValidActiveSellerGroupId()
        // can validate membership from this point on (LOG-475, Gap 1). If the
        // previously-stored cookie didn't match any candidate, selectSellerGroup
        // already fell back to a legitimate one above — this call just makes
        // sure the corrected value (and only it) is ever trusted downstream.
        setValidSellerGroupIds(
          resolvedProduct,
          getProductCandidates(portalMe, resolvedProduct).map(
            (context) => context.id,
          ),
        );
        setActiveSellerGroupId(sellerGroup.id, resolvedProduct);
        api.defaults.headers.common[ACTIVE_SELLER_GROUP_HEADER] =
          sellerGroup.id;

        bootstrappedContext = {
          portalMe,
          product: resolvedProduct,
          isHomologation: sellerGroup.is_homologation,
          sellerGroup,
        };

        return bootstrappedContext;
      } catch (error) {
        if (error instanceof DashboardContextRedirect) {
          throw error;
        }

        throw new DashboardContextRedirect(getPortalUrl());
      } finally {
        bootstrapPromise = null;
      }
    })();

    return bootstrapPromise;
  };
